
import moment from 'moment-timezone';
import { fromJS } from 'immutable';

import { logError } from '../errorUtils';





const LOCALE_DATE = 'll';


const LOCALE_DAY_AND_DATE = 'dddd, LL';


const MONTH_YEAR = 'MMMM YYYY';


const YEAR_MONTH_DAY = 'YYYY-MM-DD';


const TIME_ONLY = 'hh:mm';


const MERIDIEM_ONLY = 'A';


const LOCALE_TIME = 'LT';


const LOCALE_DATE_TIME = 'lll';

export const FORMATS = {
    LOCALE_DATE,
    LOCALE_DAY_AND_DATE,
    MONTH_YEAR,
    YEAR_MONTH_DAY,
    TIME_ONLY,
    MERIDIEM_ONLY,
    LOCALE_TIME,
    LOCALE_DATE_TIME
};


export const toMomentYearMonthDay = date => moment(date, FORMATS.YEAR_MONTH_DAY);


export const toMomentTime = time => moment(time, FORMATS.LOCALE_TIME);


export const toMomentMonthYear = (month, year) => moment(`${month} ${year}`, 'MM YY');


export const mergeDateTime = (date, time, timezone) => {
    const merged = moment(date)
        .hours(time.hours())
        .minutes(time.minutes());

    
    if (!timezone || !moment.tz.zone(timezone)) return merged;

    return moment.tz(
        merged.format(FORMATS.LOCALE_DATE_TIME),
        FORMATS.LOCALE_DATE_TIME,
        timezone
    );
};


export const toDate = date => date.toDate();


export const toYearMonthDayString = date => date.format(FORMATS.YEAR_MONTH_DAY);


export const toLocalizedDateString = date => date.format(FORMATS.LOCALE_DATE);


export const toLocalizedTimeString = time => time.format(FORMATS.LOCALE_TIME);


export const toShortTimeString = time => time.format(FORMATS.TIME_ONLY);


export const toLocalizedDateTimeString = (date, time, timezone) => {
    const dateMoment = moment.isMoment(date) ? date : toMomentYearMonthDay(date);
    const timeMoment = moment.isMoment(time) ? time : toMomentTime(time);
    const stringFormat = `${FORMATS.LOCALE_DATE_TIME} z`;

    return mergeDateTime(dateMoment, timeMoment, timezone).format(stringFormat);
};


export const convertTimeZone = (dateTimeString, timeZone = 'UTC') => {
    
    const actualTimeZone = moment.tz.zone(timeZone) ? timeZone : 'UTC';

    
    const timezoneAwareDateTime = moment.tz(dateTimeString, 'YYYY-MM-DD HH:mm:ss', actualTimeZone);

    
    return timezoneAwareDateTime.utc().format();
};

export const isSameDay = (firstDate, secondDate) => {
    if (!moment.isMoment(firstDate) || !moment.isMoment(secondDate)) return false;
    return firstDate.isSame(secondDate, 'day');
};

export const isInclusivelyAfterDay = (firstDate, secondDate) => {
    if (!moment.isMoment(firstDate) || !moment.isMoment(secondDate)) return false;
    return firstDate.isAfter(secondDate) || isSameDay(firstDate, secondDate);
};

export const isInclusivelyBeforeDay = (firstDate, secondDate) => {
    if (!moment.isMoment(firstDate) || !moment.isMoment(secondDate)) return false;
    return firstDate.isBefore(secondDate) || isSameDay(firstDate, secondDate);
};

export const getCurrentDateTime = () => moment();


export const getMinute = time => time.minute();


export const getHour = time => time.hour();


export const getMeridiem = time => moment(time).format(FORMATS.MERIDIEM_ONLY);


export const getTimeZoneAbbr = timezone => moment.tz(timezone).zoneAbbr();



const commonLocales = fromJS({
    
    fr: () => new Promise(resolve => require.ensure(['moment'], require => resolve(require('moment/locale/fr')))),
    
    en: () => Promise.resolve(),
    es: () => new Promise(resolve => require.ensure(['moment'], require => resolve(require('moment/locale/es')))),
    sv: () => new Promise(resolve => require.ensure(['moment'], require => resolve(require('moment/locale/sv'))))
});



const loadMomentLocale = localeToLoad => new Promise((resolve, reject) => {
    require.ensure(
        ['moment'],
        (require) => {
            require(`moment/locale/${localeToLoad}`);
            resolve();
        },
        (error) => { 
            reject(error);
        }
    );
});



export function momentLocaleImport(locale) {
    const language = locale.split('-')[0];

    
    if (commonLocales.has(locale)) {
        return commonLocales.get(locale)();
    }

    
    return loadMomentLocale(locale)
        .catch(() => {
            
            if (commonLocales.has(language)) {
                return commonLocales.get(language)();
            }
            throw Error(`Language ${language} not in commonLocales.`);
        })
        
        .catch(() => loadMomentLocale(language))
        
        .catch(error => logError(`Neither locale '${locale}' nor language '${language} 
            were able to be loaded for moment localeData: ${error}`));
}

export default {
    toMomentTime,
    toMomentMonthYear,
    mergeDateTime,
    toLocalizedDateString,
    toShortTimeString,
    toLocalizedDateTimeString,
    convertTimeZone,
    isSameDay,
    isInclusivelyAfterDay,
    isInclusivelyBeforeDay,
    getCurrentDateTime,
    getMeridiem,
    momentLocaleImport,
    FORMATS
};
